import type { ApiPaginatedResponse, ApiResponse, ISalesperson } from '@bcube/shared';
import { api } from '@/lib/api';

export type SalespersonAccount = {
  id: string;
  name: string;
  document: { type: string; value: string };
  sectors: string[];
};
export type Seller = Omit<ISalesperson, 'clientCount'>;
export type Page<T> = { docs: T[]; total: number; page: number; pages: number; limit: number };
export type SalespersonHistory = {
  id: string;
  action: string;
  createdAt: string;
  user: { name: string; email: string };
  metadata: { field: string; before: unknown; after: unknown }[];
};
export type SalespersonFilters = {
  name?: string;
  cpf?: string;
  email?: string;
  page?: number;
  limit?: number;
};

export const salespersonApi = {
  async candidates(name: string) {
    const { data } = await api.get<ApiPaginatedResponse<ISalesperson>>(
      '/commercial/salespersons/candidates',
      { params: { name, limit: 30 } },
    );
    return data.data;
  },
  async create(userId: string) {
    await api.post('/commercial/salespersons', { userId });
  },
  async delete(id: string) {
    await api.delete(`/commercial/salespersons/${id}`);
  },
  async list(params: SalespersonFilters) {
    const { data } = await api.get<ApiPaginatedResponse<ISalesperson>>('/commercial/salespersons', {
      params,
    });
    return { docs: data.data, ...data.meta };
  },
  async accounts(
    id: string,
    params: { page: number; limit?: number; search?: string; available?: boolean },
  ) {
    const { data } = await api.get<
      ApiResponse<{ salesperson: Seller; accounts: Page<SalespersonAccount> }>
    >(`/commercial/salespersons/${id}/accounts`, { params });
    return data.data;
  },
  async addAccount(id: string, accountId: string) {
    await api.post(`/commercial/salespersons/${id}/accounts`, { accountId });
  },
  async removeAccount(id: string, accountId: string) {
    await api.delete(`/commercial/salespersons/${id}/accounts/${accountId}`);
  },
  async history(id: string, page: number) {
    const { data } = await api.get<ApiPaginatedResponse<SalespersonHistory>>(
      `/commercial/salespersons/${id}/history`,
      { params: { page, limit: 10 } },
    );
    return { docs: data.data, ...data.meta };
  },
};
